import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Download, CreditCard, Globe, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Helmet } from "react-helmet-async";

//const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = 'https://starnivesh.onrender.com';

export default function BooksStore() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [userEmail, setUserEmail] = useState("");
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [razorpayKey, setRazorpayKey] = useState("");
  const [paypalClientId, setPaypalClientId] = useState("");
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchBooks();
    fetchRazorpayKey();
    fetchPayPalClientId();
    loadRazorpayScript();
  }, []);

  const fetchRazorpayKey = async () => {
    try {
      //const response = await axios.get(`${BACKEND_URL}/api/payment/razorpay/key`);
      const response = await axios.get(`${BACKEND_URL}/api/payment/razorpay/key`);
      setRazorpayKey(response.data.key);
    } catch (error) {
      console.error("Error fetching Razorpay key:", error);
    }
  };

  const fetchPayPalClientId = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payment/paypal/client-id`);
      setPaypalClientId(response.data.client_id);
    } catch (error) {
      console.error("Error fetching PayPal client ID:", error);
    }
  };

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/books`);
      setBooks(response.data);
    } catch (error) {
      console.error("Error fetching books:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      fetchBooks();
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/books/search/${encodeURIComponent(query)}`);
      setBooks(response.data);
    } catch (error) {
      console.error("Error searching books:", error);
    } finally {
      setSearching(false);
    }
  };

  const loadRazorpayScript = () => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
  };

  const handleBuyBook = (book) => {
    setSelectedBook(book);
    setShowPaymentDialog(true);
  };

  const handleRazorpayPayment = async () => {
    if (!userEmail) {
      toast.error("Please enter your email address");
      return;
    }

    try {
      // Create Razorpay order
      const orderResponse = await axios.post(`${BACKEND_URL}/api/payment/razorpay/create-order`, {
        book_id: selectedBook.id,
        amount: selectedBook.price_inr,
        currency: "INR"
      });

      const options = {
        key: razorpayKey || "rzp_test_placeholder",
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: "Star Nivesh",
        description: selectedBook.title,
        order_id: orderResponse.data.id,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await axios.post(`${BACKEND_URL}/api/payment/razorpay/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              book_id: selectedBook.id,
              user_email: userEmail
            });

            toast.success("Payment successful! Downloading your book...");
            window.location.href = `${BACKEND_URL}${verifyResponse.data.download_url}`;
            setShowPaymentDialog(false);
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          email: userEmail
        },
        theme: {
          color: "#F5A623"
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error("Failed to initiate payment. Please try again.");
      console.error("Razorpay error:", error);
    }
  };

  const createPayPalOrder = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payment/paypal/create-order`, {
        book_id: selectedBook.id
      });
      return response.data.id;
    } catch (error) {
      console.error("PayPal order creation error:", error);
      toast.error("Failed to create PayPal order");
      throw error;
    }
  };

  const onPayPalApprove = async (data) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payment/paypal/capture`, {
        order_id: data.orderID,
        book_id: selectedBook.id,
        user_email: userEmail
      });

      toast.success("Payment successful! Downloading your book...");
      window.location.href = `${BACKEND_URL}${response.data.download_url}`;
      setShowPaymentDialog(false);
    } catch (error) {
      toast.error("Payment capture failed");
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-text-secondary text-xl" data-testid="books-loading">Loading books...</p>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" style={{backgroundColor: "#2C3540"}}>
      <Helmet>
        <title>Books Store - Investment & Finance Books | Star Nivesh</title>
        <meta name="description" content="Browse and purchase comprehensive guides on mutual fund investing and wealth creation. Digital books available for instant download." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Books Store - Star Nivesh" />
        <meta property="og:description" content="Investment and finance books for mutual fund investors" />
        <meta property="og:type" content="website" />
      </Helmet>
      <section className="py-20" style={{backgroundColor: '#1F2937'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-16">
            <div>
              <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4" data-testid="books-title">
                Books <span style={{color: "#F5A623"}}>Store</span>
              </h1>
              <p className="text-xl text-text-secondary max-w-2xl">
                Comprehensive guides on mutual fund investing and wealth creation. Purchase and download instantly.
              </p>
            </div>

            {/* Search Bar - Top Right */}
            <div className="w-full md:w-96 relative flex-shrink-0">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-muted h-5 w-5" />
              <Input
                type="text"
                placeholder="Search books..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                //className="pl-12 py-3 text-base bg-background-paper border-white/10 text-text-primary placeholder:text-text-muted" --changing colour
                className="pl-12 py-3 text-base bg-gray-800 border-gray-50 text-text-primary placeholder:text-text-muted"
                data-testid="books-search-input"
              />
              {searching && <p className="text-text-muted text-sm mt-1">Searching...</p>}
              {searchQuery && !searching && (
                <p className="text-text-secondary text-sm mt-1">
                  {books.length} result{books.length !== 1 ? 's' : ''} found
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {books.map((book, index) => (
              <div key={book.id} className="glass-effect rounded-2xl overflow-hidden hover:-translate-y-1 transition-all" data-testid={`book-card-${index}`}>
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={book.cover_image_url}
                    alt={book.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-text-primary mb-3">
                    {book.title}
                  </h3>
                  <p className="text-text-secondary mb-6 line-clamp-3">
                    {book.description}
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-text-muted text-sm">India</p>
                      <p className="text-2xl font-bold text-primary">₹{book.price_inr}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-text-muted text-sm">International</p>
                      <p className="text-2xl font-bold text-secondary">${book.price_usd}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleBuyBook(book)}
                    className="btn-primary w-full"
                    data-testid={`buy-button-${index}`}
                  >
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    Buy Now
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {books.length === 0 && (
            <div className="text-center py-20">
              <p className="text-text-secondary text-xl">No books available at the moment. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-background border border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-text-primary">
              Complete Your Purchase
            </DialogTitle>
          </DialogHeader>
          
          {selectedBook && (
            <div className="space-y-6">
              <div className="glass-effect p-4 rounded-xl">
                <h3 className="font-semibold text-text-primary mb-2">{selectedBook.title}</h3>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">India:</span>
                  <span className="text-primary font-bold">₹{selectedBook.price_inr}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">International:</span>
                  <span className="text-secondary font-bold">${selectedBook.price_usd}</span>
                </div>
              </div>

              <div>
                <Label className="text-text-primary mb-2">Your Email</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="w-full"
                  required
                />
                <p className="text-xs text-text-muted mt-1">Book will be sent to this email</p>
              </div>

              <div>
                <Label className="text-text-primary mb-3">Select Payment Method</Label>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                  <div className="flex items-center space-x-2 glass-effect p-4 rounded-lg cursor-pointer hover:border-primary/30 transition-all">
                    <RadioGroupItem value="razorpay" id="razorpay" />
                    <Label htmlFor="razorpay" className="flex items-center cursor-pointer flex-1">
                      <CreditCard className="h-5 w-5 text-primary mr-2" />
                      <div className="flex-1">
                        <p className="font-semibold text-text-primary">Razorpay</p>
                        <p className="text-xs text-text-muted">For Indian customers (₹{selectedBook.price_inr})</p>
                      </div>
                    </Label>
                  </div>
                  
                  <div className={`flex items-center space-x-2 glass-effect p-4 rounded-lg transition-all ${paypalClientId ? 'cursor-pointer hover:border-secondary/30' : 'opacity-50 cursor-not-allowed'}`}>
                    <RadioGroupItem value="paypal" id="paypal" disabled={!paypalClientId} />
                    <Label htmlFor="paypal" className={`flex items-center flex-1 ${paypalClientId ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                      <Globe className="h-5 w-5 text-secondary mr-2" />
                      <div className="flex-1">
                        <p className="font-semibold text-text-primary">PayPal</p>
                        <p className="text-xs text-text-muted">
                          {paypalClientId 
                            ? `For international customers ($${selectedBook.price_usd})`
                            : 'Not configured - use Razorpay'}
                        </p>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {paymentMethod === "razorpay" ? (
                <Button 
                  onClick={handleRazorpayPayment}
                  className="btn-primary w-full"
                  disabled={!userEmail}
                >
                  Pay ₹{selectedBook.price_inr} with Razorpay
                </Button>
              ) : (
                <div className="w-full">
                  {userEmail ? (
                    paypalClientId ? (
                      <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "USD" }}>
                        <PayPalButtons
                          createOrder={createPayPalOrder}
                          onApprove={onPayPalApprove}
                          style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                        />
                      </PayPalScriptProvider>
                    ) : (
                      <div className="text-center p-4 bg-background-dark rounded-lg">
                        <p className="text-text-muted text-sm mb-2">PayPal is not configured yet</p>
                        <p className="text-text-secondary text-xs">Please contact support or use Razorpay</p>
                      </div>
                    )
                  ) : (
                    <p className="text-center text-text-muted text-sm">Please enter your email to continue</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}